import { useNavigate } from 'react-router-dom';
import { UrlUpdateType, useQueryParam } from 'use-query-params';

interface IPropsOption {
  paramName?: string;
  isQueryParam?: boolean;
  urlSearchParams?: Record<string, string>;
  resetParams?: readonly string[];
}

/**
 * Хук для инициализации табов
 * @param tabsKeys - массив ключей табов
 * @param options - Опциональный параметры табов
 * @param options.paramName - имя параметра в URL (по умолчанию 'second_tab')
 * @param options.isQueryParam - флаг взаимодейстсвие табов с query параметрами
 * @param options.urlSearchParams - query Параметры которые нужно вставить при переходе на страницу
 * @param options.resetParams - ключи URL, которые смена таба сбрасывает. По умолчанию
 * только `page`: новый таб начинается с первой страницы. Остальные параметры (фильтры,
 * `limit`) переживают переключение — раньше строка запроса собиралась с нуля, и после
 * смены таба форма пересобиралась из пустого URL, то есть фильтры молча терялись
 */
export const useTabsInit = <T extends string>(
  tabsKeys: readonly T[] | T[],
  {
    paramName = 'tab',
    isQueryParam = false,
    urlSearchParams = {},
    resetParams = ['page']
  }: IPropsOption = {}
) => {
  const [activeTab, setActiveTab] = useQueryParam<T>(paramName);
  const navigate = useNavigate();

  // Проверяем, что активный таб является валидным значением из массива tabsKeys
  const safeActiveTab =
    activeTab && tabsKeys.includes(activeTab) ? activeTab : tabsKeys[0];

  return {
    safeActiveTab,
    /**
     * Изменения таба
     * @param value - Значение
     * @param urlUpdateType - тип
     */
    setActiveTab: (value: T, urlUpdateType?: UrlUpdateType) => {
      if (isQueryParam) {
        setActiveTab(value, urlUpdateType);
      } else {
        // Исходим из текущей строки запроса, а не собираем с нуля
        const searchParams = new URLSearchParams(window.location.search);

        resetParams.forEach(key => searchParams.delete(key));
        Object.entries(urlSearchParams).forEach(([key, paramValue]) =>
          searchParams.set(key, paramValue)
        );
        searchParams.set(paramName, value);

        navigate(`${window.location.pathname}?${searchParams}`);
      }
    }
  };
};
